import axios from 'axios'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { SaasActionRequiredType } from 'src/component-modal/SaasActionRequiredModal/SaasActionRequiredModal'
import { BulkUploadResponse } from 'src/models/bulkUpload.models'
import { VirtualCardPushTaskListResponse } from 'src/models/virtualCardPushTask.models'
import { RootState } from 'src/redux/createRootReducer'
import {
  selectAuthedFormApiRequestConfig,
  selectAuthedJsonApiRequestConfig,
} from 'src/redux/selectors/axios.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'

export const GET_BULK_UPLOAD_PUSH_STATUS_INIT =
  'data/virtualCardPushTasks/GET_BULK_UPLOAD_PUSH_STATUS_INIT'
export const GET_BULK_UPLOAD_PUSH_STATUS_SUCCESS =
  'data/virtualCardPushTasks/GET_BULK_UPLOAD_PUSH_STATUS_SUCCESS'
export const GET_BULK_UPLOAD_PUSH_STATUS_FAILURE =
  'data/virtualCardPushTasks/GET_BULK_UPLOAD_PUSH_STATUS_FAILURE'

export type GetBulkUploadPushStatusSuccessPayload = {
  bulkVirtualCardUpload: BulkUploadResponse
}

export type GetBulkUploadPushStatusErrorPayload = BackendErrorPayload<string>

export interface GetBulkUploadPushStatusInitAction {
  type: typeof GET_BULK_UPLOAD_PUSH_STATUS_INIT
}
export interface GetBulkUploadPushStatusSuccessAction {
  type: typeof GET_BULK_UPLOAD_PUSH_STATUS_SUCCESS
  payload: GetBulkUploadPushStatusSuccessPayload
}

export interface GetBulkUploadPushStatusFailureAction {
  type: typeof GET_BULK_UPLOAD_PUSH_STATUS_FAILURE
  payload: GetBulkUploadPushStatusErrorPayload
}

export type GetBulkUploadStatusActions =
  | GetBulkUploadPushStatusInitAction
  | GetBulkUploadPushStatusSuccessAction
  | GetBulkUploadPushStatusFailureAction

const createGetBulkUploadPushStatusInitAction =
  (): GetBulkUploadPushStatusInitAction => ({
    type: GET_BULK_UPLOAD_PUSH_STATUS_INIT,
  })
const createGetBulkUploadPushStatusSuccessAction = (
  payload: GetBulkUploadPushStatusSuccessPayload,
): GetBulkUploadPushStatusSuccessAction => {
  return {
    type: GET_BULK_UPLOAD_PUSH_STATUS_SUCCESS,
    payload,
  }
}
const createGetBulkUploadPushStatusFailureAction = (
  payload: GetBulkUploadPushStatusErrorPayload,
): GetBulkUploadPushStatusFailureAction => ({
  type: GET_BULK_UPLOAD_PUSH_STATUS_FAILURE,
  payload,
})

export const POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_INIT =
  'data/virtualCardPushTasks/POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_INIT'
export const POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_SUCCESS =
  'data/virtualCardPushTasks/POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_SUCCESS'
export const POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_FAILURE =
  'data/virtualCardPushTasks/POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_FAILURE'

export type PostCreditCardBulkVirtualCardsSuccessPayload = {
  bulkVirtualCardPush: {
    tasks: VirtualCardPushTaskListResponse[]
  }
  invalidEmails: string[]
}

// Status + exceptionType are threaded through so the reducer can detect
// paywall responses (402 + a known SaasActionRequiredType) and skip its own
// error rendering — the global SaasActionRequiredModal / DowngradedToStarterModal
// (App.tsx axios interceptor) already shows the message in that case.
export type PostCreditCardBulkVirtualCardsErrorPayload =
  BackendErrorPayload<string> & {
    status: number
    exceptionType?: SaasActionRequiredType
  }

export interface PostCreditCardBulkVirtualCardsInitAction {
  type: typeof POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_INIT
}
export interface PostCreditCardBulkVirtualCardsSuccessAction {
  type: typeof POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_SUCCESS
  payload: PostCreditCardBulkVirtualCardsSuccessPayload
}
export interface PostCreditCardBulkVirtualCardsFailureAction {
  type: typeof POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_FAILURE
  payload: PostCreditCardBulkVirtualCardsErrorPayload
}

export type PostCreditCardBulkVirtualCardsActions =
  | PostCreditCardBulkVirtualCardsInitAction
  | PostCreditCardBulkVirtualCardsSuccessAction
  | PostCreditCardBulkVirtualCardsFailureAction

const createPostCreditCardBulkVirtualCardsInitAction =
  (): PostCreditCardBulkVirtualCardsInitAction => ({
    type: POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_INIT,
  })
const createPostCreditCardBulkVirtualCardsSuccessAction = (
  payload: PostCreditCardBulkVirtualCardsSuccessPayload,
): PostCreditCardBulkVirtualCardsSuccessAction => ({
  type: POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_SUCCESS,
  payload,
})
const createPostCreditCardBulkVirtualCardsFailureAction = (
  body: BackendErrorPayload<string>,
  status: number,
): PostCreditCardBulkVirtualCardsFailureAction => ({
  type: POST_CREDIT_CARD_BULK_VIRTUAL_CARDS_FAILURE,
  payload: { ...body, status },
})

// TODO: switch the message sent based on targets? or switch in the creator
export const postCreditCardBulkVirtualCards = (
  creditCardId: string,
  formData: FormData,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostCreditCardBulkVirtualCardsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostCreditCardBulkVirtualCardsActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostCreditCardBulkVirtualCardsInitAction())
    return axios
      .post<PostCreditCardBulkVirtualCardsSuccessPayload>(
        `/creditcards/${creditCardId}/bulkvirtualcardpush`,
        formData,
        selectAuthedFormApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createPostCreditCardBulkVirtualCardsSuccessAction(response.data),
          )
        },
        (err) => {
          dispatch(
            createPostCreditCardBulkVirtualCardsFailureAction(
              err.response.data,
              err.response.status,
            ),
          )
        },
      )
  }
}

export const getBulkUploadPushStatus = (
  bulkUploadId: string,
): ThunkAction<Promise<void>, RootState, void, GetBulkUploadStatusActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetBulkUploadStatusActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetBulkUploadPushStatusInitAction())
    return axios
      .get<GetBulkUploadPushStatusSuccessPayload>(
        `/bulkvirtualcarduploads/${bulkUploadId}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createGetBulkUploadPushStatusSuccessAction(response.data))
        },
        (err) => {
          dispatch(
            createGetBulkUploadPushStatusFailureAction(err.response.data),
          )
        },
      )
  }
}
